'use client';
import * as React from 'react';
import theme from '@clad-ui/theme';
import { cx, noop } from '../../utils';
import simulateChangeEvent from '../../utils/_simulateChangeEvent';
import Info from '../../icons/Info';
import inputClasses, { Container, inputFocusClasses, InputWrapper, Paragraph, TooltipWrapper, ValidationIcon, LeftIcon, } from './Input.styles';
import ShapeIcon from '../ShapeIcon/ShapeIcon';
import { CircleCheckFilled, CloseFilled, WarningFilled, Hide, Unhide } from '../../icons';
const { sizes, colors } = theme;
/**
 * Text Input component that has floating label, caption, and info tooltip.
 *
 * `✅ Stable`
 */
const Input = ({ id = '', name = '', type = 'text', label = '', value = '', onChange = noop, onBlur = noop, onFocus = noop, disabled = false, required = false, status = 'default', helptext = '', inputRef, allowClear = true, showPassword = false, tooltip = '', inputMode = 'text', width = 'full', characterLimit = 0, onKeyDown = noop, onKeyUp = noop, onClear = noop, showValidationIcon = false, icon, placeholder = '', helptextClassName = '', ...props }) => {
    const { useState, useRef, useEffect } = React;
    const [isEnoughSpace, setIsEnoughSpace] = useState(true);
    const [paddingRight, setPaddingRight] = useState(0);
    const tooltipIconRef = useRef(null);
    const ref = useRef(null);
    const showPasswordRef = useRef(null);
    // inputRef can be passed as RefCallback or RefObject, so in case inputRef is
    // a callback (for example react-hook-form's Controller), useImperativeHandle is needed
    React.useImperativeHandle(inputRef, () => ref === null || ref === void 0 ? void 0 : ref.current);
    // check if viewport has enough space for tooltip
    useEffect(() => {
        const tooltipIconCurrent = tooltipIconRef === null || tooltipIconRef === void 0 ? void 0 : tooltipIconRef.current;
        if (tooltipIconCurrent) {
            const tooltipNumericWidth = parseInt(sizes.$sm, 10);
            const rightSpace = window.innerWidth - tooltipIconCurrent.getBoundingClientRect().right;
            if (rightSpace < tooltipNumericWidth)
                setIsEnoughSpace(false);
            else
                setIsEnoughSpace(true);
        }
    }, [tooltip]);
    useEffect(() => {
        if (showPasswordRef.current && showPassword) {
            const buttonWidth = showPasswordRef.current.offsetWidth;
            setPaddingRight(buttonWidth);
        }
    }, []);
    const className = cx(inputClasses, inputFocusClasses, value && 'hasValue', !label && 'noLabel', required && 'required', (allowClear || tooltip || showValidationIcon) && 'showRightIcon', placeholder && 'hasPlaceholder');
    const handleClearInput = (e) => {
        if (allowClear) {
            simulateChangeEvent(ref, '');
        }
        if (onClear) {
            onClear(e);
        }
    };
    const [isShowIconPassword, setIsShowIconPassword] = useState(false);
    const IconPassword = isShowIconPassword ? Unhide : Hide;
    const handleShowPassword = () => {
        const currentInput = ref === null || ref === void 0 ? void 0 : ref.current;
        const passwordToggle = showPasswordRef.current;
        if (currentInput && passwordToggle && currentInput.type === 'password') {
            currentInput.type = 'text';
            setIsShowIconPassword(true);
        }
        else if (currentInput && passwordToggle) {
            currentInput.type = 'password';
            setIsShowIconPassword(false);
        }
    };
    let keyStroke;
    let selectAllCombination = false;
    const getKeyStroke = (event) => {
        keyStroke = event.key.toLowerCase(event.key);
        // metaKey for mac, ctrlKey for windows
        if ((event.metaKey && keyStroke === 'a') || (event.ctrlKey && keyStroke === 'a')) {
            selectAllCombination = true;
        }
    };
    const handleKeyDown = (e) => {
        if (type === 'number') {
            getKeyStroke(e);
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    };
    // if user input is invalid, input value with type="number" will be an empty string
    // in this case we prevent calling onChange and keep the previous value
    // delete, backspace keys and ctrl + a combination followed by '-', '+' keys are still allowed
    const onNumberInputChange = (event) => {
        if (event.target.value !== '' ||
            keyStroke === 'backspace' ||
            keyStroke === 'delete' ||
            (selectAllCombination && keyStroke === '-') ||
            (selectAllCombination && keyStroke === '+')) {
            onChange(event);
            selectAllCombination = false;
        }
    };
    // worst case is that the value is wrong from the start and we don't have previous value to keep
    // it stucks there leaving input value="" and will not trigger onChange while typing
    // for example: --2345
    // so we manually trigger onChange and force it to clear the wrong values
    const onNumberInputBlur = (event) => {
        onBlur(event);
        if (event.target.value === '') {
            // manually trigger onChange
            simulateChangeEvent(ref, '');
        }
    };
    const attrs = {
        className,
        type,
        onFocus,
        value,
        inputMode,
        onKeyUp,
        id: id && id,
        name: name && name,
        disabled: disabled && true,
        maxLength: characterLimit !== 0 ? characterLimit : undefined,
        placeholder: placeholder && placeholder,
    };
    if (id)
        attrs.id = id;
    if (name)
        attrs.name = name;
    if (disabled)
        attrs.disabled = true;
    if (characterLimit)
        attrs.maxLength = characterLimit;
    if (placeholder)
        attrs.placeholder = placeholder;
    // The clear/showPassword will not be able to displayed at the same time.
    const isAllowClear = !showPassword && allowClear && value;
    const isShowPassword = type === 'password' && showPassword && value;
    const isWithError = status === 'error';
    const isWithTooltip = tooltip && (allowClear ? !value : true);
    const containerWidth = sizes[width] || 'initial';
    const wrapperClassname = cx(disabled && 'disabled', isWithError && 'error', showPassword && 'showPassword', icon && 'hasLeftIcon');
    const tooltipClassname = cx(isWithTooltip && isEnoughSpace && 'tooltipRight', isWithTooltip && !isEnoughSpace && 'tooltipLeft');
    return (<Container containerWidth={containerWidth} data-clad="Input">
      <InputWrapper className={wrapperClassname} paddingRight={paddingRight}>
        {icon ? <LeftIcon>{icon}</LeftIcon> : null}
        <input {...attrs} onBlur={type === 'number' ? onNumberInputBlur : onBlur} onChange={type === 'number' ? onNumberInputChange : onChange} onKeyDown={handleKeyDown} ref={ref} {...props}/>
        {!!label && <label htmlFor={id}>{label}</label>}
        {!!isAllowClear && (<button tabIndex={-1} type="button" onClick={handleClearInput}>
            <CloseFilled width="20px" height="30px" color={colors.iconTertiary}/>
          </button>)}
        {!!isShowPassword && (<button tabIndex={-1} type="button" onClick={handleShowPassword} ref={showPasswordRef}>
            <ValidationIcon>
              <ShapeIcon bg="none" size="lg">
                <IconPassword className="password-icon"/>
              </ShapeIcon>
            </ValidationIcon>
          </button>)}
        {!!isWithTooltip && (<TooltipWrapper className={tooltipClassname}>
            <span ref={tooltipIconRef}>
              <Info width="20px" height="20px" color={colors.iconTertiary}/>
            </span>
            <p>{tooltip}</p>
          </TooltipWrapper>)}

        {showValidationIcon ? (<ValidationIcon>
            {status === 'error' ? (<ShapeIcon bg="none" color="iconError" size="lg">
                <WarningFilled />
              </ShapeIcon>) : status === 'success' ? (<ShapeIcon bg="none" color="iconSuccess" size="lg">
                <CircleCheckFilled />
              </ShapeIcon>) : null}
          </ValidationIcon>) : null}
      </InputWrapper>
      {helptext ? <Paragraph className={helptextClassName}>{helptext}</Paragraph> : null}
    </Container>);
};
export default Input;
